import React, { ReactNode, useState } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { MoreHoriz } from '@mui/icons-material';

export interface ContentPreviewCardMenuAction {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  destructive?: boolean;
  disabled?: boolean;
}

interface ContentPreviewCardProps {
  children: ReactNode;
  menuAriaLabel: string;
  media?: ReactNode;
  footer?: ReactNode;
  onClick?: () => void;
  menuActions?: ContentPreviewCardMenuAction[];
  sx?: SxProps<Theme>;
  actionAreaSx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
  footerSx?: SxProps<Theme>;
}

const ContentPreviewCard: React.FC<ContentPreviewCardProps> = ({
  children,
  menuAriaLabel,
  media,
  footer,
  onClick,
  menuActions,
  sx,
  actionAreaSx,
  contentSx,
  footerSx,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const hasMenu = Boolean(menuActions?.length);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  return (
    <Card
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        ...sx,
      }}
    >
      {hasMenu && (
        <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2 }}>
          <IconButton
            size="small"
            aria-label={menuAriaLabel}
            onClick={handleOpenMenu}
            sx={{
              bgcolor: 'rgba(255,250,245,0.88)',
              '&:hover': { bgcolor: 'rgba(255,250,245,1)' },
            }}
          >
            <MoreHoriz />
          </IconButton>
          <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleCloseMenu}>
            {menuActions?.map((action) => (
              <MenuItem
                key={action.label}
                disabled={action.disabled}
                onClick={(event) => {
                  event.stopPropagation();
                  handleCloseMenu();
                  action.onClick();
                }}
                sx={action.destructive ? { color: 'error.main' } : undefined}
              >
                {action.icon && <Box component="span" sx={{ display: 'inline-flex', mr: 1, color: 'inherit' }}>{action.icon}</Box>}
                {action.label}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      )}

      <CardActionArea
        component="div"
        disabled={!onClick}
        onClick={onClick}
        sx={{
          display: 'block',
          flexGrow: 1,
          textAlign: 'inherit',
          ...actionAreaSx,
        }}
      >
        {media}
        <CardContent sx={contentSx}>{children}</CardContent>
      </CardActionArea>

      {footer && <Box sx={footerSx}>{footer}</Box>}
    </Card>
  );
};

export default ContentPreviewCard;